/**
 * Utilitários para gerenciamento de notificações
 * @module notification
 */

import { supabase } from './supabase';
import { getFCMToken, requestNotificationPermission } from './firebase';

/**
 * Registra o token FCM no banco de dados
 * @param token Token FCM do dispositivo
 * @returns {Promise<boolean>} true se registrado com sucesso, false caso contrário
 */
export async function registerFCMToken(token: string): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const { error } = await supabase
      .from('user_fcm_tokens')
      .upsert({
        user_id: session.user.id,
        fcm_token: token,
        device_info: navigator.userAgent,
        created_at: new Date().toISOString()
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao registrar token FCM:', error);
    return false;
  }
}

/**
 * Solicita permissão para notificações push
 * @returns {Promise<boolean>} true se permissão concedida, false caso contrário
 */
export async function requestNotificationPermission(): Promise<boolean> {
  try {
    if (!('Notification' in window)) {
      console.log('Este navegador não suporta notificações desktop');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  } catch (error) {
    console.error('Erro ao solicitar permissão para notificações:', error);
    return false;
  }
}

/**
 * Marca uma notificação como lida
 * @param id ID da notificação
 * @returns {Promise<boolean>} true se marcada como lida com sucesso, false caso contrário
 */
export async function markNotificationAsRead(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao marcar notificação como lida:', error);
    return false;
  }
}

/**
 * Marca todas as notificações como lidas
 * @returns {Promise<boolean>} true se marcadas como lidas com sucesso, false caso contrário
 */
export async function markAllNotificationsAsRead(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('is_read', false);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao marcar todas notificações como lidas:', error);
    return false;
  }
}

/**
 * Verifica a conectividade com o Supabase
 * @returns {Promise<boolean>} true se conectado, false caso contrário
 */
async function checkSupabaseConnectivity(): Promise<boolean> {
  try {
    const { data, error } = await supabase.from('notifications').select('id').limit(1);
    return !error && data !== null;
  } catch {
    return false;
  }
}

/**
 * Obtém o número de notificações não lidas com retry e timeout
 * @returns {Promise<number>} Número de notificações não lidas
 * @throws {Error} Com mensagem específica sobre o tipo de erro encontrado
 */
export async function getUnreadNotificationsCount(retries = 3): Promise<number> {
  const timeout = 8000; // Aumentado para 8 segundos
  let attempt = 0;

  // Verifica conectividade primeiro
  try {
    const isConnected = await checkSupabaseConnectivity();
    if (!isConnected) {
      console.warn('Não foi possível conectar ao servidor');
      return 0; // Retorna 0 em vez de lançar erro
    }
  } catch (error) {
    console.error('Erro ao verificar conectividade:', error);
    return 0; // Retorna 0 em caso de erro
  }

  while (attempt < retries) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const { data, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact' })
        .eq('is_read', false)
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      if (error) {
        if (error.code === 'PGRST301') {
          console.warn('Sessão expirada');
          return 0; // Retorna 0 em vez de lançar erro
        }
        throw error;
      }

      return data?.length || 0;
    } catch (error) {
      attempt++;
      
      if (error.name === 'AbortError') {
        console.warn(`Timeout ao buscar notificações (tentativa ${attempt}/${retries})`);
        if (attempt === retries) {
          console.error('O servidor está demorando para responder');
          return 0; // Retorna 0 em vez de lançar erro
        }
      } else {
        console.error(`Erro ao buscar notificações (tentativa ${attempt}/${retries}):`, error);
        if (attempt === retries) {
          if (error.message?.includes('fetch')) {
            console.error('Não foi possível conectar ao servidor');
            return 0; // Retorna 0 em vez de lançar erro
          }
          return 0; // Retorna 0 para qualquer outro erro
        }
      }

      // Exponential backoff com jitter
      const baseDelay = Math.pow(2, attempt) * 1000;
      const jitter = Math.random() * 1000;
      await new Promise(resolve => setTimeout(resolve, baseDelay + jitter));
    }
  }

  return 0; // Fallback final
}

/**
 * Configura o serviço de notificações
 * Deve ser chamado na inicialização do aplicativo
 */
export async function setupNotifications(): Promise<void> {
  try {
    // Solicitar permissão para notificações
    const hasPermission = await requestNotificationPermission();
    
    if (!hasPermission) {
      console.log('Permissão para notificações não concedida');
      return;
    }

    // Obter token FCM
    const token = await getFCMToken();
    if (token) {
      await registerFCMToken(token);
    }

    // Registrar service worker para notificações em background
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registrado com sucesso:', registration);
      } catch (error) {
        console.error('Erro ao registrar Service Worker:', error);
      }
    }
  } catch (error) {
    console.error('Erro ao configurar notificações:', error);
  }
}

/**
 * Obtém todas as notificações do usuário
 * @param limit Limite de notificações a serem retornadas
 * @returns {Promise<Array>} Lista de notificações
 */
export async function getNotifications(limit = 20): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Erro ao obter notificações:', error);
    return [];
  }
}